import React, { useState, useEffect } from "react";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { LoadingOverlay } from "@/components/ui/loading-spinner";
import { useConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Edit2,
  BookOpen,
  Copy,
  Trash2,
  Tag,
  Calendar,
  BookText,
  User,
  FileText,
  Building,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowRightLeft,
  MapPin,
} from "lucide-react";
import { catalogApi, type CatalogItemCopy, type UnifiedSearchResponse } from "@/lib/api/catalog";
import { CopyLifecycleActions } from "@/components/catalog/CopyLifecycleActions";
import { JournalDetailsBridge, isJournalBiblio } from "@/components/catalog/JournalDetailsBridge";
import { WithdrawnCopyBanner } from "@/components/catalog/WithdrawnCopyBanner";
import { circulationApi } from "@/lib/api/circulation";
import type { Branch } from "@/lib/api/branches";
import { useActiveBranches } from "@/hooks/useActiveBranches";
import { useTransferCart } from "@/contexts/TransferCartContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import {
  formatPhysicalDescription,
  formatTitleWithEdition,
  formatAccessionNumber,
  formatNumberWithCommas,
  getAcquisitionModeLabel,
} from "@/lib/utils/catalog-formatting";

// Status color mapping
const getStatusColor = (status: string) => {
  switch (status?.toUpperCase()) {
    case "AVAILABLE":
      return "bg-green-100 text-green-700 border-green-200";
    case "CHECKED_OUT":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "LOST":
      return "bg-red-100 text-red-700 border-red-200";
    case "DAMAGED":
      return "bg-orange-100 text-orange-700 border-orange-200";
    case "IN_REPAIR":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    case "ON_HOLD":
      return "bg-purple-100 text-purple-700 border-purple-200";
    case "WITHDRAWN":
      return "bg-amber-100 text-amber-700 border-amber-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const getStatusIcon = (status: string) => {
  switch (status?.toUpperCase()) {
    case "AVAILABLE":
      return <CheckCircle className="w-3.5 h-3.5" />;
    case "CHECKED_OUT":
      return <Clock className="w-3.5 h-3.5" />;
    case "LOST":
      return <XCircle className="w-3.5 h-3.5" />;
    default:
      return <AlertCircle className="w-3.5 h-3.5" />;
  }
};

export function BookDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [bookData, setBookData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [placingHold, setPlacingHold] = useState(false);

  // Transfer queue (cart)
  const { data: branches = [] } = useActiveBranches();
  const { addItem, removeItem, isInCart } = useTransferCart();

  const { confirm, ConfirmDialog: ConfirmDialogComponent } = useConfirmDialog();
  const { hasPermission } = useAccessControl();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { tenant } = useTenant();
  const canEdit = hasPermission("catalog.edit") || hasPermission("book.edit");
  const canDelete = hasPermission("catalog.delete") || hasPermission("book.delete");
  const canEditCopy = canEdit;
  const canDeleteCopy = canDelete;
  const canPlaceHold = hasPermission("catalog.requestHold") || hasPermission("catalog.reserve");

  useEffect(() => {
    if (id) {
      loadBookDetails();
    }
  }, [id]);

  const loadBookDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await catalogApi.getBiblio(id!);
      setBookData(data);
    } catch (err: any) {
      console.error("Failed to load book details:", err);
      setError(err.response?.data?.message || "Failed to load book details");
      toast.error("Failed to load book details");
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceHold = async () => {
    if (!bookData?.id) return;
    setPlacingHold(true);
    try {
      await circulationApi.placeHold({ biblioId: bookData.id });
      toast.success("Hold placed successfully! You'll be notified when it's ready.");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to place hold");
    } finally {
      setPlacingHold(false);
    }
  };

  const handleDelete = () => {
    confirm({
      title: "Delete Book",
      description: (
        <div className="space-y-2">
          <p>
            Are you sure you want to delete <strong>"{bookData?.title}"</strong>?
          </p>
          <p className="text-sm text-gray-500">
            This will permanently delete the bibliographic record and all associated item copies.
            This action cannot be undone.
          </p>
        </div>
      ),
      variant: "danger",
      confirmLabel: "Delete Book",
      onConfirm: async () => {
        try {
          setDeleting(true);
          const biblioId = id!;
          await catalogApi.deleteBiblio(biblioId);
          if (tenant?.id) {
            queryClient.setQueriesData<UnifiedSearchResponse | undefined>(
              ["catalog", "unified", tenant.id],
              (old) => {
                if (!old?.data) return old;
                const data = old.data.filter((r) => !(r.id === biblioId && r.type === "physical"));
                return { ...old, data, total: Math.max(0, (old.total ?? 0) - 1) };
              }
            );
            await queryClient.invalidateQueries(["catalog", "unified", tenant.id]);
          }
          toast.success("Book deleted successfully!");
          navigate("/catalog");
        } catch (err: any) {
          toast.error(err.response?.data?.message || "Failed to delete book");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingOverlay size="lg" label="Loading book details..." />
      </div>
    );
  }

  if (error || !bookData) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Book Details"
          description="View bibliographic and item information"
          breadcrumbItems={[{ label: "Catalog", href: "/catalog" }, { label: "Book Details" }]}
          showSearch={false}
        />
        <Card className="border border-gray-200">
          <CardContent className="py-16 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Book Not Found</h3>
            <p className="mb-6 text-sm text-gray-500">
              {error || "The requested book could not be found."}
            </p>
            <Button onClick={() => navigate("/catalog")}>Back to Catalog</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const availableCopies =
    bookData.Items?.filter((i: any) => i.statusCode === "AVAILABLE").length || 0;
  const totalCopies = bookData.Items?.length || 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Book Details"
        description="View bibliographic and item information"
        breadcrumbItems={[{ label: "Catalog", href: "/catalog" }, { label: bookData.title }]}
        showSearch={false}
      />

      {/* Action Buttons */}
      {(canEdit || canDelete || isJournalBiblio(bookData)) && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <JournalDetailsBridge biblio={bookData} />
          {canEdit && (
            <Button
              variant="outline"
              onClick={() => navigate(`/catalog/physical/${bookData.id}/edit`)}
              className="gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={deleting}
              className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Cover and Quick Info */}
        <div className="space-y-6">
          {/* Cover Image Card */}
          <Card className="border border-gray-200 overflow-hidden">
            <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center aspect-[3/4]">
              {bookData.coverUrl ? (
                <img
                  src={bookData.coverUrl}
                  alt={bookData.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : (
                <div className="flex flex-col items-center gap-4 text-gray-400">
                  <BookOpen className="w-16 h-16" />
                  <span className="text-sm">No Cover Available</span>
                </div>
              )}
            </div>
          </Card>

          {/* Quick Stats */}
          <Card className="border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-900">Availability</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Copies</span>
                <span className="font-semibold text-gray-900">
                  {formatNumberWithCommas(totalCopies)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Available</span>
                <Badge
                  className={cn(
                    "border",
                    availableCopies > 0
                      ? "bg-green-100 text-green-700 border-green-200"
                      : "bg-red-100 text-red-700 border-red-200"
                  )}
                >
                  {formatNumberWithCommas(availableCopies)} of {formatNumberWithCommas(totalCopies)}
                </Badge>
              </div>
              {canPlaceHold && (
                <Button
                  className="w-full mt-3"
                  size="sm"
                  variant={availableCopies === 0 ? "default" : "outline"}
                  onClick={handlePlaceHold}
                  disabled={placingHold}
                >
                  <Clock className="w-4 h-4 mr-1.5" />
                  {placingHold ? "Placing Hold..." : "Place Hold"}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Classification */}
          <Card className="border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-900">
                Classification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* LC Classification hidden for now: the cataloging input sheet only captures
                  call number. The value is still stored and used for call number suggestions. */}
              {/* <div>
                <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  LC Classification
                </Label>
                <p className="font-mono text-sm mt-1">{bookData.lcClassification || "—"}</p>
              </div> */}
              <div>
                <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Call Number
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <p className="font-mono text-sm">{bookData.callNumber || "—"}</p>
                  {bookData.callNumber && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => copyToClipboard(bookData.callNumber, "Call number")}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ISBN
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <p className="font-mono text-sm">{bookData.isbn || "—"}</p>
                  {bookData.isbn && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => copyToClipboard(bookData.isbn, "ISBN")}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Bibliographic Information */}
          <Card className="border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <BookOpen className="w-5 h-5 text-primary" />
                Bibliographic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </Label>
                <p className="text-xl font-semibold text-gray-900 mt-1">
                  {formatTitleWithEdition(bookData.title, bookData.subtitle, bookData.edition)}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {bookData.authors?.length > 0
                      ? "Authors"
                      : bookData.editors?.length > 0
                        ? "Editors"
                        : "Authors"}
                  </Label>
                  <p className="mt-1">
                    {bookData.authors && bookData.authors.length > 0
                      ? bookData.authors.join(", ")
                      : bookData.editors && bookData.editors.length > 0
                        ? bookData.editors.join(", ")
                        : "—"}
                  </p>
                  {bookData.editors?.length > 0 && bookData.authors?.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Editor-in-Chief: {bookData.editors[0]}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
                    <Building className="w-3 h-3" />
                    Publisher
                  </Label>
                  <p className="mt-1">{bookData.publisher || "—"}</p>
                  {bookData.placeOfPublication && (
                    <p className="text-sm text-gray-500 mt-0.5">{bookData.placeOfPublication}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Published
                  </Label>
                  <p className="mt-1">{bookData.publishedDate || "—"}</p>
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    Physical Description
                  </Label>
                  <p className="mt-1 font-mono text-sm">
                    {formatPhysicalDescription(
                      bookData.pages,
                      bookData.illustrations,
                      bookData.size
                    ) || "—"}
                  </p>
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
                    <BookText className="w-3 h-3" />
                    Binding
                  </Label>
                  <p className="mt-1">
                    {bookData.bindingType === "hbk"
                      ? "Hardback"
                      : bookData.bindingType === "pbk"
                        ? "Paperback"
                        : bookData.bindingType === "audiobook"
                          ? "Audiobook"
                          : bookData.bindingType === "journal"
                            ? "Journal"
                            : "—"}
                  </p>
                </div>
              </div>

              {/* Series & Identifiers Row */}
              {(bookData.seriesInformation || bookData.issn || bookData.doi) && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {bookData.seriesInformation && (
                    <div>
                      <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Series
                      </Label>
                      <p className="mt-1">{bookData.seriesInformation}</p>
                    </div>
                  )}
                  {bookData.issn && (
                    <div>
                      <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ISSN
                      </Label>
                      <p className="mt-1 font-mono text-sm">{bookData.issn}</p>
                    </div>
                  )}
                  {bookData.doi && (
                    <div>
                      <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        DOI
                      </Label>
                      <p className="mt-1 font-mono text-sm break-all">{bookData.doi}</p>
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
                  <Tag className="w-3 h-3" />
                  Subjects
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {bookData.subjects && bookData.subjects.length > 0 ? (
                    bookData.subjects.map((subject: string, idx: number) => (
                      <Badge key={idx} variant="outline" className="bg-gray-50 text-gray-700">
                        {subject}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">No subjects assigned</span>
                  )}
                </div>
              </div>

              {bookData.description && (
                <div>
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </Label>
                  <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                    {bookData.description}
                  </p>
                </div>
              )}

              <div className="pt-4 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </Label>
                    <p className="mt-1 text-gray-600">
                      {new Date(bookData.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </Label>
                    <p className="mt-1 text-gray-600">
                      {new Date(bookData.updatedAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Item Copies */}
          <Card className="border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <Package className="w-5 h-5 text-primary" />
                  Item Copies ({formatNumberWithCommas(totalCopies)})
                </span>
                {canEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/catalog/physical/${id}/edit`)}
                    className="gap-1.5"
                  >
                    <Package className="w-4 h-4" />
                    Add Copies
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bookData.Items && bookData.Items.length > 0 ? (
                <div className="space-y-3">
                  {bookData.Items.map((item: any) => (
                    <div
                      key={item.id}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 bg-white rounded-lg border border-gray-200 text-sm font-medium text-gray-600">
                            #{item.copyNumber}
                          </div>
                          <div>
                            <p className="font-mono text-sm text-gray-900">{item.barcode}</p>
                            <p className="text-xs text-gray-500">
                              Accession:{" "}
                              {formatAccessionNumber(item.accessionNumber) || item.accessionNumber}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <Badge
                            className={cn(
                              "border flex items-center gap-1",
                              getStatusColor(item.statusCode)
                            )}
                          >
                            {getStatusIcon(item.statusCode)}
                            {item.statusCode?.replace("_", " ")}
                          </Badge>
                          <CopyLifecycleActions
                            item={item as CatalogItemCopy}
                            canEdit={canEditCopy}
                            canDelete={canDeleteCopy}
                            onUpdated={(updated) => {
                              setBookData((prev: any) => ({
                                ...prev,
                                Items: prev.Items.map((i: any) =>
                                  i.id === updated.id ? { ...i, ...updated } : i
                                ),
                              }));
                            }}
                            onRemoved={(itemId) => {
                              setBookData((prev: any) => ({
                                ...prev,
                                Items: prev.Items.filter((i: any) => i.id !== itemId),
                              }));
                            }}
                          />
                          {branches.length > 1 && (
                            <Button
                              variant={isInCart(item.id) ? "default" : "outline"}
                              size="sm"
                              className={cn(
                                "h-7 gap-1 text-xs",
                                isInCart(item.id) && "bg-primary hover:bg-primary/90"
                              )}
                              onClick={() => {
                                if (isInCart(item.id)) {
                                  removeItem(item.id);
                                } else {
                                  addItem({
                                    itemId: item.id,
                                    biblioId: bookData.id,
                                    biblioTitle: bookData.title,
                                    barcode: item.barcode,
                                    copyNumber: item.copyNumber,
                                    currentBranchId: item.branchId || item.branch?.id,
                                    currentBranchName: item.branch?.name,
                                    toBranchId: "",
                                    toBranchName: "",
                                  });
                                }
                              }}
                            >
                              <ArrowRightLeft className="w-3 h-3" />
                              {isInCart(item.id) ? "Remove from Queue" : "Add to Transfer Queue"}
                            </Button>
                          )}
                        </div>
                      </div>
                      {(item.statusCode === "WITHDRAWN" || item.withdrawnAt) && (
                        <div className="mb-3">
                          <WithdrawnCopyBanner item={item} />
                        </div>
                      )}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Type:</span>{" "}
                          <span className="font-medium">{item.itemType?.name || "—"}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Location:</span>{" "}
                          <span className="font-medium">{item.location?.name || "—"}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Collection:</span>{" "}
                          <span className="font-medium">{item.collection?.name || "—"}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-gray-400" />
                          <span className="text-gray-500">Branch:</span>{" "}
                          <span className="font-medium">{item.branch?.name || "—"}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Condition:</span>{" "}
                          <span className="font-medium">{item.conditionCode || "—"}</span>
                        </div>
                        {item.acquisitionMode && (
                          <div>
                            <span className="text-gray-500">Acquired:</span>{" "}
                            <span className="font-medium">
                              {getAcquisitionModeLabel(item.acquisitionMode)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">No item copies available</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Transfer queue hint banner — shown when at least one copy is queued */}
          {bookData.Items?.some((item: any) => isInCart(item.id)) && (
            <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <ArrowRightLeft className="w-4 h-4 text-primary flex-shrink-0" />
              <p className="text-sm text-primary flex-1">
                {bookData.Items.filter((item: any) => isInCart(item.id)).length} cop
                {bookData.Items.filter((item: any) => isInCart(item.id)).length === 1
                  ? "y"
                  : "ies"}{" "}
                added to transfer queue. Open the <strong>Transfer Queue</strong> button in the
                header to set destinations and submit.
              </p>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialogComponent />
    </div>
  );
}
